import { useMutation } from '@apollo/client'
import { Button, Checkbox, Col, Form, Input, InputNumber, message, Row, Spin } from 'antd'
import * as PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoginModal } from '../../../../../context/loginModalContext'
import { showAuthError } from '../../../../../shared/utils/webHelpers'
import { StyledDrawer } from '../../../../common/CommonStyles'
import { v4 as uuidv4 } from 'uuid'
import { useForceUpdate } from '../../../../../shared/hooks/useForceUpdate'
import CreateNewItemExtraButtons from '../../../../common/CreateNewItemExtraButtons'
import { useNavigate } from 'react-router-dom'
import NameField from '../../../../common/formFields/NameField'
import ADD_NEW_REFERRAL_SOURCE_ITEM
  from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/referralSources/addNewReferralSourceItem'
import EDIT_REFERRAL_SOURCE
  from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/referralSources/editReferralSource'
import UserColorPicker from '../../Users/UserColorPicker'
import DeleteReferralSourceButton from './DeleteReferralSourceButton'

const ReferralSourceForm = props => {
  const [forceUpdate, handleForceUpdate] = useForceUpdate()

  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { setLoginModalVisible } = useLoginModal()
  const { t } = useTranslation()

  const { formVisible, selectedItem, handleClose } = props
  const { validateFields, resetFields, setFieldsValue } = form

  const forPatients = Form.useWatch('forPatients', form)
  const forUsers = Form.useWatch('forUsers', form)

  useEffect(() => {
    if (formVisible) {
      resetFields()
      if (selectedItem.new) {
        setFieldsValue({
          color: `#${Math.floor(Math.random() * 16777215).toString(16)}`
        })
      }
    }
  }, [formVisible])

  const [addNewItem, { loading }] = useMutation(ADD_NEW_REFERRAL_SOURCE_ITEM)
  const [editItem, { loading: loadingEdit }] = useMutation(EDIT_REFERRAL_SOURCE)

  const generatedID = useMemo(uuidv4, [formVisible, forceUpdate])

  const handleSubmit = key => {
    validateFields()
      .then(async values => {
        try {
          let item

          if (selectedItem.new) {
            item = await addNewItem({
              variables: {
                ...values,
                generatedID
              }
            })

            if (key === 'view') {
              navigate(`/settings/types/referral-source/${generatedID}`)
              handleClose()
            } else if (key === 'new') {
              resetFields()
              handleForceUpdate()
              setFieldsValue({
                color: `#${Math.floor(Math.random() * 16777215).toString(16)}`
              })
            } else {
              handleClose(item.data.addNewReferralSourceItem)
            }
          } else {
            item = await editItem({
              variables: {
                ...values,
                referralSource: selectedItem.id
              }
            })
            handleClose()
          }

          message.success(`${selectedItem.new ? t('common.createdSuccessfully') : t('common.updatedSuccessfully')}`)
          if (props.handleAnimateRow) {
            props.handleAnimateRow(
              (item.data.addNewReferralSourceItem && item.data.addNewReferralSourceItem.id) ||
                (item.data.editReferralSource && item.data.editReferralSource.id),
              selectedItem.new
            )
          }
        } catch (e) {
          showAuthError(e, t, setLoginModalVisible)
        }
      })
      .catch(({ errorFields }) => {
        form.scrollToField(errorFields?.[0]?.name)
      })
  }

  return (
    <StyledDrawer
      title={!selectedItem.new ? t('common.edit') : t('patients.newItem')}
      open={formVisible}
      onClose={handleClose}
      destroyOnClose
      footer={
        <div className="flex-container justify-between">
          {!selectedItem.new ? (
            <DeleteReferralSourceButton
              selectedItem={selectedItem}
              handleAnimateRow={props.handleAnimateRow}
              handleClose={handleClose}
            />
          ) : selectedItem.new && selectedItem.fromDrawer ? (
            <CreateNewItemExtraButtons handleSubmit={handleSubmit} disabled={loading} />
          ) : null}
          <Button loading={loading || loadingEdit} htmlType="submit" type="primary" form={`${generatedID}referralSourceForm`}>
            {selectedItem.new ? t('common.create') : t('common.update')}
          </Button>
        </div>
      }
    >
      <Spin spinning={loading || loadingEdit}>
        <Form
          layout="vertical"
          scrollToFirstError
          form={form}
          name={`${generatedID}referralSourceForm`}
          onFinish={handleSubmit}
          initialValues={{
            ...selectedItem,
            forceSubtype: selectedItem.forceSubtype || false,
            forPatients: selectedItem.forPatients || false,
            forUsers: selectedItem.forUsers || false,
            showForPatients: selectedItem.showForPatients || false
          }}
        >
          <Row type="flex" gutter={16}>
            <Col span={16}>
              <NameField required min={1} max={100} autoFocus autoComplete="" />
            </Col>
            <Col span={8}>
              <Form.Item name="color" label={t('settings.groups.color')}>
                <UserColorPicker />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item label={t('app.referralUrl')} name="referralUrl">
                <Input maxLength={500} />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item label={t('app.referralId')} name="referralId">
                <Input maxLength={500} />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item name="forceSubtype" valuePropName="checked" label={t('app.forceSubtype')}>
                <Checkbox />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item name="showForPatients" valuePropName="checked" label={t('app.showForPatients')}>
                <Checkbox />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item name="forPatients" valuePropName="checked" label={t('app.forPatientsLabel')}>
                <Checkbox disabled={!selectedItem.new || !!forUsers} />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item name="forUsers" valuePropName="checked" label={t('app.forUsersLabel')}>
                <Checkbox disabled={!selectedItem.new || !!forPatients} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="order" label={t('app.order')}>
                <InputNumber min={0} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </StyledDrawer>
  )
}

ReferralSourceForm.propTypes = {
  selectedItem: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  formVisible: PropTypes.bool.isRequired,
  handleAnimateRow: PropTypes.func
}

export default ReferralSourceForm
